<template>
  <div>
    <popup-add-legal-document
      ref="popupAddLegalDocument"
      @documentSelected="addLegalDocument"
    />
    <vx-card no-shadow>
      <div class="vx-row mb-6 mt-2">
        <div class="vx-col sm:w-4/5 w-full">
          <vs-row>
            <h2 style="font-size: revert; font-weight: 500">
              {{ bookingForm.formName }}
            </h2>
          </vs-row>
        </div>
      </div>

      <div class="vx-row mb-6">
        <div class="vx-col sm:w-1/5 w-full">
          <span>Form id</span>
          <br />
        </div>
        <div class="vx-col sm:w-2/5 w-full">
          <code>{{ bookingForm.id }} </code>
        </div>
      </div>

      <div class="vx-row mb-6">
        <div class="vx-col sm:w-1/5 w-full">
          <span>Form name</span>
          <br />
          <small>Not shown to guests</small>
        </div>
        <div class="vx-col sm:w-2/5 w-full">
          <vs-input v-model="bookingForm.formName"></vs-input>
        </div>
      </div>

      <div class="vx-row mb-6">
        <div class="vx-col sm:w-1/5 w-full">
          <span>Public form name</span>
          <br />
          <small>Displayed to guests at the top of the form</small>
        </div>
        <div class="vx-col sm:w-2/5 w-full">
          <vs-input v-model="bookingForm.publicFormName"></vs-input>
        </div>
      </div>

      <div class="vx-row mb-6">
        <div class="vx-col sm:w-1/5 w-full">
          <span>Submitted title text</span>
          <br />
          <small>The top line shown to guests when they submit the form</small>
        </div>
        <div class="vx-col sm:w-2/5 w-full">
          <vs-textarea v-model="bookingForm.submittedTitle"></vs-textarea>
        </div>
      </div>

      <div class="vx-row mb-6">
        <div class="vx-col sm:w-1/5 w-full">
          <span>Submitted text</span>
          <br />
          <small
            >The bottom, smaller, line shown guests when they submit the form
          </small>
        </div>
        <div class="vx-col sm:w-2/5 w-full">
          <vs-textarea v-model="bookingForm.submittedText"></vs-textarea>
        </div>
      </div>

      <div class="vx-row mb-6">
        <div class="vx-col sm:w-1/5 w-full">
          <span>Currency</span>
          <br />
          <small>The currency of this form</small>
        </div>
        <div class="vx-col sm:w-2/5 w-full">
          <vs-select
            v-model="bookingForm.currency"
            :label="bookingForm.currency"
          >
            <vs-select-item value="DKK" text="DKK" />
            <vs-select-item value="NOK" text="NOK" />
            <vs-select-item value="EUR" text="EUR" />
            <vs-select-item value="GBP" text="GBP" />
          </vs-select>
        </div>
      </div>

      <div class="vx-row mb-6">
        <div class="vx-col sm:w-1/5 w-full">
          <span>Minimum spend</span>
          <br />
          <small
            >The minimum amount the booking request must total to. Set to 0 for
            no minimum spent</small
          >
        </div>
        <div class="vx-col sm:w-2/5 w-full">
          <vs-input-number
            :label="bookingForm.currency"
            v-model="bookingForm.minimumSpend"
            step="5"
            class="justify-start bg-transparent"
          ></vs-input-number>
        </div>
      </div>

      <div class="vx-row mb-6">
        <div class="vx-col sm:w-1/5 w-full">
          <span>Legal documents</span>
          <br />
          <small>Legal documents attached to the form</small>
        </div>
        <div
          class="vx-col mb-8 pb-8"
          v-if="!legalDocuments || legalDocuments.length == 0"
        >
          <Loading />
        </div>
        <div v-else class="vx-col sm:w-2/5 w-full">
          <vs-card
            class="max-w-md"
            v-for="doc in bookingForm.legalDocuments"
            :key="doc.docId"
          >
            <div slot="header">
              <vs-row vs-justify="space-between">
                <h6>
                  <span v-if="legalDocInfo(doc.docId)">{{
                    legalDocInfo(doc.docId).name
                  }}</span>
                  <span v-else style="color: red"
                    >Error: legal document has been deleted.</span
                  >
                </h6>
                <div>
                  <feather-icon
                    icon="TrashIcon"
                    svgClasses="w-5 h-5 hover:text-danger stroke-current"
                    class="ml-2 cursor-pointer"
                    @click.stop="removeLegalDoc(doc.docId)"
                  />
                </div>
              </vs-row>
            </div>
            <div>
              <vs-row class="mb-4">
                <vs-col vs-w="4">Document id</vs-col>
                <vs-col vs-w="8">
                  <code>{{ doc.docId }}</code>
                </vs-col>
              </vs-row>
              <vs-row>
                <vs-col vs-w="4">Document name</vs-col>
                <vs-col vs-w="8">
                  {{
                    legalDocInfo(doc.docId)
                      ? legalDocInfo(doc.docId).name
                      : "Legal document no longer exists"
                  }}
                </vs-col>
              </vs-row>
              <vs-row class="mt-4">
                <vs-col vs-w="4">Consents</vs-col>
                <vs-col vs-w="8" v-if="legalDocInfo(doc.docId)">
                  {{ legalDocInfo(doc.docId).consentTypes.join(", ") }}
                </vs-col>
              </vs-row>
              <vs-row class="mt-4">
                <vs-col vs-w="4">Consent checkbox text</vs-col>
                <vs-col vs-w="8">
                  <vs-input v-model="doc.displayText" class="w-full" />
                </vs-col>
              </vs-row>
              <vs-row class="mt-4">
                <vs-col vs-w="4">Consent required?</vs-col>
                <vs-col vs-w="8">
                  <vs-switch v-model="doc.isRequired" />
                </vs-col>
              </vs-row>
            </div>
          </vs-card>

          <vs-button @click="$refs.popupAddLegalDocument.show(legalDocuments)"
            >Add legal document</vs-button
          >
        </div>
      </div>

      <div class="vx-row mb-6">
        <div class="vx-col sm:w-1/5 w-full">
          <span>Venue Email</span>
        </div>
        <div class="vx-col sm:w-2/5 w-full">
          <vs-select v-model="bookingForm.venueMail">
            <vs-select-item value="VenuePrimary" text="Venue Primary" />
            <vs-select-item value="Booking" text="Booking" />
            <vs-select-item value="LostAndFound" text="Lost And Found" />
          </vs-select>
        </div>
      </div>

      <div class="vx-row mb-6">
        <div class="vx-col sm:w-1/5 w-full">
          <span>Last updated</span>
        </div>
        <div class="vx-col sm:w-2/5 w-full">
          <span
            >{{ bookingForm.updatedAction.timestamp | formatDate }} by
            {{ bookingForm.updatedAction.userDisplayName }}</span
          >
        </div>
      </div>
    </vx-card>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import PopupAddLegalDocument from "./Components/PopupAddLegalDocument.vue";

export default {
  components: {
    PopupAddLegalDocument,
  },
  props: {
    bookingForm: {
      type: Object,
      default: () => ({}),
    },
  },
  methods: {
    legalDocInfo(docId) {
      return this.legalDocuments.find((doc) => doc.id === docId);
    },

    addLegalDocument(legalDocumentId) {
      console.log(
        "addLegalDocument",
        legalDocumentId,
        this.bookingForm.legalDocuments
      );
      if (
        this.bookingForm.legalDocuments.some((e) => e.docId == legalDocumentId)
      ) {
        this.$vs.notify({
          title: "Legal document already added",
          text: "This legal document is already added to the form",
          color: "danger",
        });
        return;
      }
      this.bookingForm.legalDocuments.push({
        docId: legalDocumentId,
        displayText: "I agree to the terms and conditions",
        isRequired: true,
        isAccepted: false,
      });
    },

    removeLegalDoc(legalDocumentId) {
      this.bookingForm.legalDocuments = this.bookingForm.legalDocuments.filter(
        (doc) => doc.docId !== legalDocumentId
      );
    },
  },
  computed: {
    ...mapGetters({
      legalDocuments: "legalDocuments/documents",
    }),
  },
};
</script>
